import logger from '@/lib/logger';
import { useState, useEffect } from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Button } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { useMeasurementWizardStore } from '@/store/useMeasurementWizardStore';
import { ClinicalSurveyFields } from './patient/ClinicalSurveyFields';
import { testService } from '@/services/api/testService';
import type { SurveyData } from '@/types/test';

interface SurveyFormData {
  survey: SurveyData;
}

interface SurveyPanelProps {
  testId?: string;
  initialSurveyData?: SurveyData;
}

/**
 * Normalize survey data from backend (PascalCase) to frontend (camelCase)
 * Backend returns: AgeBand, BiologicalSex, etc.
 * Frontend expects: ageBand, biologicalSex, etc.
 */
function normalizeSurveyData(data: any): SurveyData | null {
  if (!data || typeof data !== 'object') return null;
  
  // Check if already in camelCase format (data from store)
  if ('ageBand' in data || 'biologicalSex' in data) {
    return data as SurveyData;
  }
  
  // Convert from PascalCase (backend) to camelCase (frontend)
  return {
    ageBand: data.AgeBand || data.ageBand,
    biologicalSex: data.BiologicalSex || data.biologicalSex,
    ethnicity: data.Ethnicity || data.ethnicity,
    symptomPresence: data.SymptomPresence || data.symptomPresence,
    symptomDuration: data.SymptomDuration || data.symptomDuration,
    priorTestResult: data.PriorTestResult || data.priorTestResult,
    geographicRegion: data.GeographicRegion || data.geographicRegion,
  };
}

/**
 * SurveyPanel - Optional clinical trial survey collection
 * Collects survey data that will be attached to the test
 */
export function SurveyPanel({ testId: propsTestId, initialSurveyData }: SurveyPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const wizardTestId = useMeasurementWizardStore((s) => s.testId);
  const wizardAdditionalInfo = useMeasurementWizardStore((s) => s.additionalInfo);
  const setAdditionalInfo = useMeasurementWizardStore((s) => s.setAdditionalInfo);
  
  // Use prop testId if provided, otherwise wizard testId
  const testId = propsTestId || wizardTestId || undefined;
  
  // Use initial survey data from props if provided, otherwise from wizard store
  // Normalize the data to handle PascalCase from backend
  const rawInitialData = initialSurveyData || wizardAdditionalInfo;
  const initialData = normalizeSurveyData(rawInitialData);
  
  const { register, control, getValues, reset } = useForm<SurveyFormData>({
    defaultValues: {
      survey: initialData || {}
    }
  });

  // Update form when initial data changes (use JSON stringify to compare object values)
  useEffect(() => {
    if (initialData) {
      reset({ survey: initialData });
    }
  }, [JSON.stringify(initialData), reset]);

  const handleSave = async () => {
    const formData = getValues();
    
    if (!testId) {
      // If test hasn't been created yet, save to store (will be sent to backend when test is created)
      setAdditionalInfo(formData.survey);
      setExpanded(false);
      return;
    }

    // If test exists, save to backend immediately
    setIsSaving(true);
    try {
      await testService.updateTestAdditionalInfo(testId, formData.survey);
      setAdditionalInfo(formData.survey);
      setExpanded(false);
    } catch (error) {
      logger.error('[SurveyPanel] Failed to save survey data:', error);
      // TODO: Show error toast
    } finally {
      setIsSaving(false);
    }
  };

  const handleClear = async () => {
    if (testId) {
      setIsSaving(true);
      try {
        await testService.updateTestAdditionalInfo(testId, null);
        reset({ 
          survey: {
            ageBand: undefined,
            biologicalSex: undefined,
            ethnicity: '',
            symptomPresence: undefined,
            symptomDuration: undefined,
            priorTestResult: undefined,
            geographicRegion: '',
          }
        });
        setAdditionalInfo(null);
      } catch (error) {
        logger.error('Failed to clear survey data:', error);
        // TODO: Show error toast
      } finally {
        setIsSaving(false);
      }
    } else {
      reset({ 
        survey: {
          ageBand: undefined,
          biologicalSex: undefined,
          ethnicity: '',
          symptomPresence: undefined,
          symptomDuration: undefined,
          priorTestResult: undefined,
          geographicRegion: '',
        }
      });
      setAdditionalInfo(null);
    }
  };

  return (
    <Box>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
            <Typography variant="h6">Clinical Trial</Typography>
            {initialData && !expanded && (
              <Typography variant="caption" color="text.secondary">
                Survey data saved
              </Typography>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <ClinicalSurveyFields register={register} control={control} />
            
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleClear}
                disabled={isSaving}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Survey'}
              </Button>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
